// src/services/api.js
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.dive-connect.com/api';

// Single source of truth for the API base — also used for the SSE stream URL,
// which can't go through the axios instance.
export const API_BASE_URL = API_URL;

// Static asset host: the API base minus the trailing /api. Uploaded images are
// served from /uploads on the same origin as the API.
export const UPLOADS_BASE_URL = API_URL.replace(/\/api\/?$/, '');

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Auth services
export const authService = {
  register: (userData) => api.post('/auth/register', userData),
  login: (credentials) => api.post('/auth/login', credentials),
  getCurrentUser: () => api.get('/auth/me'),
  verifyEmail: (token) => api.get(`/auth/verify/${token}`),
  resendVerification: (data) => api.post('/auth/resend-verification', data),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (token, data) => api.post(`/auth/reset-password/${token}`, data)
};

// Trip services
export const tripService = {
  getAllTrips: (params = {}) => {
    const token = localStorage.getItem('token');
    const config = token ? { 
      headers: { Authorization: `Bearer ${token}` },
      params
    } : { params };
    
    return api.get('/trips', config);
  },
  getTripById: (id) => {
    // Create a config object for requests with auth token if available
    const token = localStorage.getItem('token');
    const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
    
    return api.get(`/trips/${id}`, config);
  },
  createTrip: (tripData) => {
    // For FormData, don't manually set Content-Type
    return api.post('/trips', tripData, {
      headers: {
        // Override default JSON content type behavior for FormData
        'Content-Type': undefined
      }
    });
  },
  updateTrip: (id, tripData) => {
    return api.put(`/trips/${id}`, tripData, {
      headers: {
        'Content-Type': undefined
      }
    });
  },
  deleteTrip: (id) => api.delete(`/trips/${id}`),
  deleteImage: (tripId, filename) => api.delete(`/trips/${tripId}/images/${filename}`),
  getTripParticipants: (id) => api.get(`/trips/${id}/participants`),
  applyForTrip: (tripId, applicationData) => api.post(`/trips/${tripId}/apply`, applicationData),
};

// Application services
export const applicationService = {
  getAllApplications: () => api.get('/applications'),
  updateApplicationStatus: (id, statusData) => api.put(`/applications/${id}/status`, statusData),
  cancelApplication: (id) => api.delete(`/applications/${id}`),
  getUserApplications: () => api.get('/applications/user/me'),
  getTripWaitingList: (tripId) => {
    const token = localStorage.getItem('token');
    const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
    return api.get(`/applications/trip/${tripId}/waiting-list`, config);
  },
};

export const userService = {
  getAllUsers: () => api.get('/users'),
  getUserDetails: (id) => api.get(`/users/${id}`),
  updateProfile: (profileData) => api.put('/users/profile', profileData),
  changePassword: (passwordData) => api.put('/users/change-password', passwordData),
  updateUserRole: (id, role) => api.put(`/users/${id}/role`, { role }),
  deleteUser: (id) => api.delete(`/users/${id}`),
};

// Stats services
export const statsService = {
  getAdminStats: () => api.get('/stats/admin'),
  getStatsByPeriod: (startDate, endDate) => api.get('/stats/period', { params: { startDate, endDate } }),
};

// Article services
export const articleService = {
  getArticles: (params) => api.get('/articles', { params }),
  getArticleBySlug: (slug) => api.get(`/articles/${slug}`),
  getFeaturedArticles: (limit = 5) => api.get('/articles/featured', { params: { limit } }),
  getRelatedArticles: (id) => api.get(`/articles/${id}/related`),
  getCategories: () => api.get('/articles/categories/list'),
  
  // Admin routes
  getAllArticlesAdmin: (params) => api.get('/articles/admin/all', { params }),
  createArticle: (articleData) => api.post('/articles', articleData, {
    headers: { 'Content-Type': undefined }
  }),
  updateArticle: (id, articleData) => api.put(`/articles/${id}`, articleData, {
    headers: { 'Content-Type': undefined }
  }),
  deleteArticle: (id) => api.delete(`/articles/${id}`),
};

// Upload services
export const uploadService = {
  uploadImage: (imageFile) => {
    const formData = new FormData();
    formData.append('image', imageFile);
    return api.post('/upload/image', formData, {
      headers: { 'Content-Type': undefined }
    });
  }
};

// Subscriber services
export const subscriberService = {
  subscribe: (data) => api.post('/subscribers/subscribe', data),
  unsubscribe: (token) => api.get(`/subscribers/unsubscribe/${token}`),
  getAllSubscribers: (status = 'all') => api.get(`/subscribers/admin/all?status=${status}`),
  deleteSubscriber: (id) => api.delete(`/subscribers/admin/${id}`),
  exportSubscribers: () => api.get('/subscribers/admin/export', { responseType: 'blob' })
};

export default api;